import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { DocumentDetailsTab } from '@/features/documents/DocumentDetailsModal/DocumentDetailsTab'
import { FormRow, FormGroup, Form } from '@/components/Form'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { type DocumentDetailsModalViewModel } from '@/features/documents/DocumentDetailsModal/DocumentDetailsModalViewModel'

export interface DocumentDetailsTabContainerProps {
  vm: DocumentDetailsModalViewModel
}

export const DocumentDetailsTabContainer = observer(
  function DocumentDetailsTabContainer({
    vm,
  }: DocumentDetailsTabContainerProps) {
    const itemSource = useMemo(
      () =>
        ItemSource.static(vm.documentTypes, (item, text) => {
          return item.name.toLowerCase().includes(text.toLowerCase())
        }),
      [vm.documentTypes],
    )
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    return (
      <DocumentDetailsTab>
        <Form onSubmit={vm.save} isSubmitDisabled={vm.isPending}>
          {vm.canEditTitleAndDescription && (
            <>
              <FormRow>
                <TextInputContainer
                  label="Title"
                  field="title"
                  model={vm}
                  placeholder={vm.title || 'Give your document a name'}
                  error={vm.validation.getError('title')}
                  readOnly={!vm.canEdit}
                  isDisabled={!vm.canEdit || vm.isPending}
                />
              </FormRow>
              <FormRow>
                <TextAreaContainer
                  label="Description"
                  maxLength={vm.descriptionLengthLimit}
                  field="description"
                  model={vm}
                  rows={3}
                  placeholder={'Optional description'}
                  readOnly={!vm.canEdit}
                  isDisabled={!vm.canEdit || vm.isPending}
                  help={`${vm.descriptionCharsRemaining} characters left`}
                />
              </FormRow>
            </>
          )}
          <FormRow>
            <FormGroup label="Type">
              <CustomSelect
                multi={false}
                {...selectProps}
                areItemsEqual={(left, right) => left?.id === right?.id}
                value={vm.selectedDocumentType}
                onChange={vm.setDocumentType}
                itemToString={(item) => item.name}
                isDisabled={!vm.canChangeDocumentType}
                testId="custom-select"
              />
            </FormGroup>
          </FormRow>
        </Form>
      </DocumentDetailsTab>
    )
  },
)
